import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Save, X } from "lucide-react";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const IconBtn = ({ onClick, children, testId, danger, title }) => (
  <button onClick={onClick} title={title} className={`p-1.5 transition-colors ${danger ? "text-[var(--ff-red)] hover:text-white" : "text-[var(--ff-text-dim)] hover:text-white"}`} data-testid={testId}>{children}</button>
);

function RuleForm({ initial, sections, onSave, onCancel, testPrefix }) {
  const [f, setF] = useState({ title: "", body: "", section_id: initial?.section_id, require_reread: true, ...initial });
  return (
    <div className="space-y-3 border border-[var(--ff-border)] p-4 bg-[var(--ff-bg)]" data-testid={`${testPrefix}-form`}>
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Policy title" className="w-full px-3 py-2 text-sm" data-testid={`${testPrefix}-title`} />
      <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={5} placeholder="Write the policy rule…" className="w-full px-3 py-2 text-sm leading-relaxed" data-testid={`${testPrefix}-body`} />
      {initial?.id && (
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <select value={f.section_id} onChange={(e) => setF({ ...f, section_id: e.target.value })} className="px-2 py-1.5" data-testid={`${testPrefix}-section`}>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.require_reread} onChange={(e) => setF({ ...f, require_reread: e.target.checked })} data-testid={`${testPrefix}-reread`} />
            Require everyone to re-read (pops up for staff, locks hosts until read)
          </label>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => onSave(f)} className="btn-primary text-xs" data-testid={`${testPrefix}-save`}><Save size={12} /> Save</button>
        <button onClick={onCancel} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid={`${testPrefix}-cancel`}><X size={12} /> Cancel</button>
      </div>
    </div>
  );
}

function RuleRow({ rule, sections, onChanged }) {
  const [editing, setEditing] = useState(false);
  const save = async (f) => {
    try {
      await api.patch(`/admin/policies/rules/${rule.id}`, { title: f.title, body: f.body, section_id: f.section_id, require_reread: f.require_reread });
      toast.success("Policy updated"); setEditing(false); onChanged();
    } catch (e) { err(e); }
  };
  const move = (direction) => api.post("/admin/policies/move", { kind: "rule", id: rule.id, direction }).then(onChanged).catch(err);
  const del = async () => {
    if (!window.confirm(`Delete "${rule.title}"?`)) return;
    try { await api.delete(`/admin/policies/rules/${rule.id}`); onChanged(); } catch (e) { err(e); }
  };
  if (editing) return <RuleForm initial={rule} sections={sections} onSave={save} onCancel={() => setEditing(false)} testPrefix={`rule-edit-${rule.id}`} />;
  return (
    <div className="flex items-start gap-3 py-3 border-t border-[var(--ff-border)]" data-testid={`admin-rule-${rule.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-display">{rule.title} <span className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)]">v{rule.version}</span></div>
        <div className="text-xs text-[var(--ff-text-dim)] line-clamp-2 whitespace-pre-wrap">{rule.body}</div>
      </div>
      <IconBtn onClick={() => move("up")} testId={`rule-up-${rule.id}`} title="Move up"><ChevronUp size={14} /></IconBtn>
      <IconBtn onClick={() => move("down")} testId={`rule-down-${rule.id}`} title="Move down"><ChevronDown size={14} /></IconBtn>
      <IconBtn onClick={() => setEditing(true)} testId={`rule-edit-${rule.id}`} title="Edit"><Pencil size={14} /></IconBtn>
      <IconBtn onClick={del} testId={`rule-delete-${rule.id}`} danger title="Delete"><Trash2 size={14} /></IconBtn>
    </div>
  );
}

function SectionCard({ section, sections, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [t, setT] = useState({ title: section.title, description: section.description || "" });
  const saveSection = async () => {
    try { await api.patch(`/admin/policies/sections/${section.id}`, t); setEditing(false); onChanged(); } catch (e) { err(e); }
  };
  const addRule = async (f) => {
    try {
      await api.post("/admin/policies/rules", { section_id: section.id, title: f.title, body: f.body });
      toast.success("Policy published — staff & hosts will be notified"); setAdding(false); onChanged();
    } catch (e) { err(e); }
  };
  const move = (direction) => api.post("/admin/policies/move", { kind: "section", id: section.id, direction }).then(onChanged).catch(err);
  const del = async () => {
    if (!window.confirm(`Delete section "${section.title}" and all ${section.rules.length} of its policies?`)) return;
    try { await api.delete(`/admin/policies/sections/${section.id}`); onChanged(); } catch (e) { err(e); }
  };
  return (
    <div className="surface-card p-5" data-testid={`admin-section-${section.id}`}>
      {editing ? (
        <div className="space-y-2 mb-3">
          <input value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid={`section-edit-title-${section.id}`} />
          <input value={t.description} onChange={(e) => setT({ ...t, description: e.target.value })} placeholder="Short description (optional)" className="w-full px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={saveSection} className="btn-primary text-xs" data-testid={`section-save-${section.id}`}><Save size={12} /> Save</button>
            <button onClick={() => setEditing(false)} className="btn-ghost inline-flex items-center gap-1.5 text-xs"><X size={12} /> Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 mb-2">
          <div className="flex-1">
            <div className="font-display text-xl">{section.title}</div>
            {section.description && <div className="text-xs text-[var(--ff-text-dim)]">{section.description}</div>}
          </div>
          <IconBtn onClick={() => move("up")} testId={`section-up-${section.id}`}><ChevronUp size={14} /></IconBtn>
          <IconBtn onClick={() => move("down")} testId={`section-down-${section.id}`}><ChevronDown size={14} /></IconBtn>
          <IconBtn onClick={() => setEditing(true)} testId={`section-edit-${section.id}`}><Pencil size={14} /></IconBtn>
          <IconBtn onClick={del} testId={`section-delete-${section.id}`} danger><Trash2 size={14} /></IconBtn>
        </div>
      )}
      {section.rules.map((r) => <RuleRow key={r.id} rule={r} sections={sections} onChanged={onChanged} />)}
      <div className="pt-3">
        {adding
          ? <RuleForm initial={{ section_id: section.id }} sections={sections} onSave={addRule} onCancel={() => setAdding(false)} testPrefix={`rule-new-${section.id}`} />
          : <button onClick={() => setAdding(true)} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid={`rule-add-${section.id}`}><Plus size={12} /> Add Policy Rule</button>}
      </div>
    </div>
  );
}

export default function PolicyEditor() {
  const [sections, setSections] = useState(null);
  const [newSec, setNewSec] = useState({ title: "", description: "" });
  const load = useCallback(() => api.get("/policies").then(({ data }) => setSections(data.sections)).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const addSection = async () => {
    if (!newSec.title.trim()) return toast.error("Give the section a title");
    try { await api.post("/admin/policies/sections", newSec); setNewSec({ title: "", description: "" }); load(); } catch (e) { err(e); }
  };
  if (!sections) return <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="policy-editor">
      <div className="surface-card p-5 flex flex-col md:flex-row gap-3">
        <input value={newSec.title} onChange={(e) => setNewSec({ ...newSec, title: e.target.value })} placeholder="New section title (e.g. Code of Conduct)" className="flex-1 px-3 py-2 text-sm" data-testid="section-new-title" />
        <input value={newSec.description} onChange={(e) => setNewSec({ ...newSec, description: e.target.value })} placeholder="Description (optional)" className="flex-1 px-3 py-2 text-sm" data-testid="section-new-description" />
        <button onClick={addSection} className="btn-primary text-xs" data-testid="section-add-btn"><Plus size={12} /> Add Section</button>
      </div>
      {sections.length === 0 && <div className="text-sm text-[var(--ff-text-dim)]">No sections yet. Add your first section above.</div>}
      {sections.map((s) => <SectionCard key={s.id} section={s} sections={sections} onChanged={load} />)}
    </div>
  );
}
